// The Tour parts come from Ark UI, so the anatomy comes from there too — Chakra has no `tour`
// counterpart yet (chakra-ui/chakra-ui#10410 is still open).
import { tourAnatomy } from '@ark-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const tourSlotRecipe = defineSlotRecipe({
  className: 'tour',
  slots: [...tourAnatomy.keys(), 'actions', 'badge', 'header', 'media', 'titleGroup', 'titleRow'] as const,
  base: {
    backdrop: {
      background: 'utilities/overlay',
      zIndex: 'dialogOverlay',
      _open: {
        animationStyle: 'fade-in',
        animationDuration: 'moderate',
      },
      // No exit animation: Ark sets `hidden` on the backdrop and the content the moment the tour
      // closes, so the node is `display: none` in the same frame and an exit animation can never
      // paint — it only leaves the animation assigned, which flashes the card back in for a frame
      // on the next render. Most visible on steps without a backdrop, where nothing masks it.
    },
    // Sits on the same layer as the backdrop and after it in DOM order, so the ring around the
    // highlighted element paints over the dimmed page.
    spotlight: {
      borderRadius: '4',
      outline: '2px solid',
      outlineColor: 'interactive/highlight',
      zIndex: 'dialogOverlay',
    },
    // Zag positions and layers this one inline, writing
    // `z-index: calc(var(--tour-layer) + var(--tour-z-index))` and expecting us to supply the base.
    // Without it the calc is invalid, the positioner falls back to `auto` — and since it opens its
    // own stacking context (`isolation: isolate`), the card's own z-index can't reach past it, so
    // the whole card ends up under the backdrop and swallows every click. The plain `zIndex` is the
    // fallback for the day Zag stops writing that inline.
    positioner: {
      '--tour-z-index': 'zIndex.dialog',
      zIndex: 'dialog',
    },
    content: {
      background: 'background/primary',
      borderRadius: '8',
      boxShadow: 'elevation/lg',
      display: 'flex',
      flexDirection: 'column',
      gap: '8',
      // Floored, and the floor is what keeps the card from talking Popper out of flipping. Zag's
      // `size` middleware runs after `flip` and writes `--available-height` from the space on the
      // chosen side; the card is the same node from step to step, so it carries that cap into the
      // next step's first measurement. A step that needs 328 measured 193 — the cap left by a
      // shorter step — decided it fitted below, and never flipped to the side that had 474 free.
      // Below this floor the card stops shrinking, so it overflows instead, which is what `flip`
      // reads to move it.
      maxHeight: 'max(var(--available-height, 100dvh), 18rem)',
      maxWidth: '100%',
      outline: 'none',
      padding: '16',
      position: 'relative',
      width: rem(360),
      zIndex: 'dialog',
      // Fade only, no scale: Zag puts `--transform-origin` at the arrow tip, so a `scale-in` grows
      // the card away from the highlight — a 360x300 card gains ~15 of height on the way in, which
      // reads as the card repositioning itself rather than appearing.
      _open: {
        animationStyle: 'fade-in',
        animationDuration: 'moderate',
      },
    },
    header: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: '16',
      justifyContent: 'space-between',
    },
    titleGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8',
    },
    // flex-start, not center: the badge is as tall as one line of the title, so it lines up with
    // the first line instead of floating beside the middle of a title that wrapped.
    titleRow: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: '8',
    },
    badge: {
      flexShrink: 0,
    },
    title: {
      color: 'text/primary',
      textStyle: 'heading/h4',
    },
    progressText: {
      color: 'text/secondary',
      textStyle: 'body/sm/regular',
    },
    description: {
      color: 'text/body',
      minHeight: 0,
      overflowY: 'auto',
      textStyle: 'body/md/regular',
    },
    // These two margins stack on the content's own 8 gap, which is how the layout spec builds the
    // spacing: 16 between the description and the visual, and 24 above the footer. The footer gets
    // nothing below it, so the card's own 16 padding is the whole gap under the buttons.
    media: {
      marginBlockStart: '8',
    },
    control: {
      alignItems: 'center',
      display: 'flex',
      gap: '16',
      justifyContent: 'space-between',
      marginBlockStart: '16',
    },
    actions: {
      display: 'flex',
      gap: '8',
      marginInlineStart: 'auto',
    },
    closeTrigger: {
      flexShrink: 0,
    },
    arrow: {
      // Popper draws the arrow as a square rotated 45°, so the visible triangle is `size * √2`
      // wide and `size / √2` tall — 11.3 gives the design's 16×8.
      '--arrow-size': rem(11.3),
      '--arrow-background': 'colors.background/primary',
      // Size and out-of-flow positioning are set here, not only by Popper's inline styles, because
      // the component keeps this element out of Zag's `hidden` (see BitkitTour): Popper derives the
      // card's distance from the anchor as `offset.mainAxis + arrowEl.clientHeight / 2`, read off
      // the DOM. A `display: none` arrow measures 0, which puts the card 6 short of the design's 12
      // on the first pass. `absolute` keeps it from adding its height to the card while it waits
      // for Popper's own inline placement.
      height: 'var(--arrow-size)',
      position: 'absolute',
      width: 'var(--arrow-size)',
    },
    arrowTip: {
      borderColor: 'border/minimal',
      borderInlineStartWidth: '1',
      borderTopWidth: '1',
    },
  },
});

export default tourSlotRecipe;
